import React, { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

const KEY = "berry-muted";

export default function SoundToggle() {
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(KEY) === "1"; } catch { return false; }
  });
  const ctxRef = useRef(null);
  const masterRef = useRef(null);
  const noiseRef = useRef(null);
  const playedRef = useRef(false);

  const ensureCtx = () => {
    if (ctxRef.current) return ctxRef.current;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    const ctx = new AC();
    const master = ctx.createGain();
    master.gain.value = muted ? 0 : 1;
    master.connect(ctx.destination);
    // pre-build a short noise buffer for the scan sweep
    const buf = ctx.createBuffer(1, ctx.sampleRate * 3, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    ctxRef.current = ctx;
    masterRef.current = master;
    noiseRef.current = buf;
    return ctx;
  };

  const playBoot = () => {
    const ctx = ensureCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") ctx.resume();
    const master = masterRef.current;
    const now = ctx.currentTime;

    // rising power-up tone
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(110, now);
    osc.frequency.exponentialRampToValueAtTime(660, now + 2.2);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.07, now + 0.45);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 2.7);
    osc.connect(g).connect(master);
    osc.start(now);
    osc.stop(now + 2.8);

    // scan sweep — bandpass-filtered noise rising in frequency
    const src = ctx.createBufferSource();
    src.buffer = noiseRef.current;
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.Q.value = 6;
    bp.frequency.setValueAtTime(380, now);
    bp.frequency.exponentialRampToValueAtTime(3200, now + 2.4);
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.0001, now);
    ng.gain.linearRampToValueAtTime(0.045, now + 0.3);
    ng.gain.linearRampToValueAtTime(0.0001, now + 2.6);
    src.connect(bp).connect(ng).connect(master);
    src.start(now);
    src.stop(now + 2.8);

    // fragment "lock-in" blips
    [0.55, 0.95, 1.4, 1.9, 2.35].forEach((t, i) => {
      const o = ctx.createOscillator();
      const bg = ctx.createGain();
      o.type = "triangle";
      o.frequency.value = 760 + i * 180;
      bg.gain.setValueAtTime(0.0001, now + t);
      bg.gain.exponentialRampToValueAtTime(0.03, now + t + 0.02);
      bg.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.13);
      o.connect(bg).connect(master);
      o.start(now + t);
      o.stop(now + t + 0.15);
    });
  };

  const bootOnce = () => {
    if (playedRef.current) return;
    playedRef.current = true;
    playBoot();
  };

  // Attempt autoplay on mount; fall back to first user interaction.
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    const ctx = ensureCtx();
    if (ctx && ctx.state === "running") {
      bootOnce();
      return;
    }
    const onInteract = () => bootOnce();
    const opts = { once: true, passive: true };
    window.addEventListener("pointerdown", onInteract, opts);
    window.addEventListener("keydown", onInteract, opts);
    window.addEventListener("scroll", onInteract, opts);
    window.addEventListener("wheel", onInteract, opts);
    return () => {
      window.removeEventListener("pointerdown", onInteract);
      window.removeEventListener("keydown", onInteract);
      window.removeEventListener("scroll", onInteract);
      window.removeEventListener("wheel", onInteract);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = () => {
    const next = !muted;
    setMuted(next);
    try { localStorage.setItem(KEY, next ? "1" : "0"); } catch {}
    const ctx = ensureCtx();
    if (ctx) {
      if (ctx.state === "suspended") ctx.resume();
      if (masterRef.current) masterRef.current.gain.value = next ? 0 : 1;
      // if unmuting and the boot hasn't played yet, play it now
      if (!next && !playedRef.current) bootOnce();
    }
  };

  return (
    <button
      onClick={toggle}
      data-testid="sound-toggle"
      aria-label={muted ? "Unmute interface sound" : "Mute interface sound"}
      aria-pressed={muted}
      className="fixed bottom-5 right-5 z-[60] flex items-center gap-2 px-3.5 py-2.5 bg-background/60 backdrop-blur-xl border border-white/10 text-white/70 hover:text-white hover:border-primary/50 transition-colors duration-300 group"
    >
      {muted ? <VolumeX size={15} /> : <Volume2 size={15} className="text-primary" />}
      <span className="hidden sm:inline font-mono-tech text-[10px] uppercase tracking-[0.2em]">
        {muted ? "Sound Off" : "Sound On"}
      </span>
      {!muted && (
        <span className="flex items-end gap-0.5 h-3" aria-hidden="true">
          <span className="w-0.5 bg-primary sound-bar" style={{ animationDelay: "0ms" }} />
          <span className="w-0.5 bg-primary sound-bar" style={{ animationDelay: "150ms" }} />
          <span className="w-0.5 bg-primary sound-bar" style={{ animationDelay: "300ms" }} />
        </span>
      )}
    </button>
  );
}
